import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Email as EmailIcon, Refresh as RefreshIcon } from '@mui/icons-material';
// import { sendEmailVerification, reload } from 'firebase-shim';
import { useAuth } from '../context/AuthContext';

interface EmailVerificationPromptProps {
  open: boolean;
  onClose: () => void;
  onVerified: () => void;
}

const EmailVerificationPrompt: React.FC<EmailVerificationPromptProps> = ({
  open,
  onClose,
  onVerified
}) => {
  const { currentUser } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleResendVerification = async () => {
    if (!currentUser) return;

    setIsResending(true);
    setError('');
    setResendSuccess(false);

    try {
      const { default: awsAuthService } = await import('../services/aws-auth');
      // For AWS Cognito, we resend based on username which is the uid
      await awsAuthService.resendConfirmationCode(currentUser.uid);
      setResendSuccess(true);
    } catch (err: any) {
      console.error('Error resending verification email:', err);
      setError(err.message || 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!currentUser) return;

    try {
      // In AWS flow, verification is confirmed by entering a code, not clicking a link.
      // However, if we are simulating the 'check status' we might just reload user or ask them to login again.
      // For now, this button might better serve as "I have verified" which redirects to login or re-fetches user.
      onVerified();
    } catch (err: any) {
      console.error('Error checking verification status:', err);
      setError('Failed to check verification status');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon color="primary" />
          <Typography variant="h6">Email Verification Required</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" paragraph>
          We've sent a verification email to <strong>{currentUser?.email}</strong>.
          Please check your inbox and click the verification link to activate your account.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Can't find the email?</strong> Check your spam folder or click "Resend Email" below.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {resendSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Verification email sent successfully! Please check your inbox.
          </Alert>
        )}

        <Box display="flex" gap={2} mt={2}>
          <Button
            variant="outlined"
            onClick={handleResendVerification}
            disabled={isResending}
            startIcon={isResending ? <CircularProgress size={20} /> : <RefreshIcon />}
            fullWidth
          >
            {isResending ? 'Sending...' : 'Resend Email'}
          </Button>

          <Button
            variant="contained"
            onClick={handleCheckVerification}
            fullWidth
          >
            I've Verified My Email
          </Button>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailVerificationPrompt;
